// src/components/WalletTopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { walletApi } from '../topup/api';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { ScrollPanel } from 'primereact/scrollpanel';
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";

const WalletTopup = () => {
     const auth = useAuth();
    const app = useApp();
    const api = useApi();
//const userId = searchParams.get('userId') || 'USER001';
const userId = auth.userId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [topupAmount, setTopupAmount] = useState(500);
  const [selectedAmount, setSelectedAmount] = useState(500);
  const [customAmount, setCustomAmount] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const presetAmounts = [100, 200, 500, 1000, 2000, 5000,10000];

  useEffect(() => {
    fetchWalletData();
    
    // Handle window resize for mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userId]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const response = await walletApi.getWallet(userId);
      setWalletData(response.data);
      setPhoneNumber(response.data.phoneNumber || '');
      
      toast.current.show({
        severity: 'info',
        summary: 'Welcome',
        detail: `Hello ${response.data.fullName}!`,
        life: 3000
      });
    } catch (error) {
      console.error('Error fetching wallet:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to load wallet data',
        life: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setTopupAmount(amount);
    setCustomAmount(null);
  };

  const handleCustomAmount = (e) => {
    const value = e.value;
    setCustomAmount(value);
    if (value > 0) {
      setTopupAmount(value);
      setSelectedAmount(null);
    }
  };

  const handleTopup = async () => {
    const amount = customAmount || selectedAmount;
    
    if (!amount || amount <= 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Invalid Amount',
        detail: 'Please select or enter a valid amount',
        life: 3000
      });
      return;
    }

    if (!phoneNumber || phoneNumber.length < 10) {
      toast.current.show({
        severity: 'warn',
        summary: 'Phone Required',
        detail: 'Please enter a valid phone number',
        life: 3000
      });
      return;
    }

    setProcessing(true);
    setShowPaymentDialog(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      const orderResponse = await walletApi.createOrder({
        userId: userId,
        amount: amount,
        currency: 'INR',
        phoneNumber: phoneNumber
      });

      const { orderId, keyId, userInfo } = orderResponse.data;

      const options = {
        key: keyId,
        amount: amount * 100,
        currency: 'INR',
        name: 'Laundry Xmart',
        description: `Wallet Top-up - ₹${amount}`,
        image: '/logo.png',
        order_id: orderId,
        handler: function(response) {
          handlePaymentSuccess(response, amount);
        },
        prefill: {
          name: userInfo.name || '',
          email: userInfo.email || '',
          contact: phoneNumber
        },
        notes: {
          userId: userId,
          amount: amount,
          phone: phoneNumber
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: function() {
            setShowPaymentDialog(false);
            setProcessing(false);
            toast.current.show({
              severity: 'info',
              summary: 'Payment Cancelled',
              detail: 'You cancelled the payment',
              life: 3000
            });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment initiation failed:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Payment Failed',
        detail: error.message || 'Failed to initiate payment',
        life: 4000
      });
      setShowPaymentDialog(false);
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (response, amount) => {
    try {
      setPaymentStatus('Verifying payment...');
      
      const verifyResponse = await walletApi.verifyPayment({
        razorpayOrderId: response.razorpay_order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpaySignature: response.razorpay_signature,
        userId: userId,
        amount: amount,
        phoneNumber: phoneNumber
      });

      if (verifyResponse.data.success) {
        setPaymentStatus('Payment successful!');
        
        toast.current.show({
          severity: 'success',
          summary: 'Payment Successful!',
          detail: `₹${amount} added to your wallet`,
          life: 5000
        });

        await fetchWalletData();

        setTimeout(() => {
          navigate(`/topup-success?phone=${phoneNumber}&amount=${amount}&payment_id=${response.razorpay_payment_id}`);
        }, 2000);
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Verification Failed',
        detail: error.response?.data?.message || 'Payment could not be verified',
        life: 5000
      });
      setPaymentStatus('Verification failed');
      setShowPaymentDialog(false);
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-d-flex p-jc-center p-ai-center p-mt-6" style={{ minHeight: '100vh' }}>
        <Card className="p-shadow-4" style={{ width: '100%', maxWidth: '600px' }}>
          <div className="p-d-flex p-flex-column p-ai-center p-gap-3">
            <ProgressSpinner />
            <Skeleton width="100%" height="100px" />
            <Skeleton width="100%" height="100px" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-d-flex p-jc-center p-ai-start p-p-3" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
      <Toast ref={toast} position="top-right" />
      
      <div className="p-d-flex p-flex-column p-gap-3" style={{ width: '100%', maxWidth: '600px' }}>
        {/* Main Topup Card */}
        <Card className="p-shadow-4">
          <div className="p-d-flex p-flex-column p-gap-3">
            {/* Header */}
            <div className="p-text-center">
              <i className="pi pi-wallet" style={{ fontSize: '2.5rem', color: '#3B82F6' }}></i>
              <h2 className="p-m-0 p-mt-2" style={{ fontSize: isMobile ? '1.5rem' : '2rem' }}>
                Wallet Top-up
              </h2>
              <p className="p-text-secondary p-m-0" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Add funds to your laundry wallet
              </p>
            </div>

            {/* User Info & Balance - Mobile Responsive */}
            <div className="p-d-flex p-flex-column p-flex-sm-row p-jc-between p-ai-stretch p-ai-sm-center p-p-3" 
                 style={{ background: '#F8F9FA', borderRadius: '8px' }}>
              <div className="p-mb-2 p-mb-sm-0">
                <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                  <i className="pi pi-user p-mr-1"></i> User
                </div>
                <div className="p-text-bold" style={{ fontSize: '1.1rem' }}>
                  {walletData?.fullName}
                </div>
                <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                  ID: {walletData?.userId}
                </div>
              </div>
              <div className="p-text-left p-text-sm-right">
                <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                  <i className="pi pi-money-bill p-mr-1"></i> Balance
                </div>
                <div className="p-text-bold" style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: '#22C55E' }}>
                  {formatCurrency(walletData?.balance || 0)}
                </div>
                <Tag severity="info" value={`Target: ${formatCurrency(walletData?.targetAmount || 0)}`} />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="p-d-block p-mb-1 p-text-secondary" style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                <i className="pi pi-phone p-mr-1"></i> Phone Number
              </label>
              <InputText
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                className="p-inputtext-lg"
                style={{ width: '100%' }}
                maxLength={10}
                keyfilter="pnum"
              />
            </div>

            {/* Amount Selection - Mobile Responsive */}
            <div>
              <label className="p-d-block p-mb-2 p-text-secondary" style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                <i className="pi pi-tag p-mr-1"></i> Select Amount (₹)
              </label>
              <div className="p-d-flex p-flex-wrap p-gap-2 p-mb-2">
                {presetAmounts.map((amount) => (
                  <Button
                    key={amount}
                    label={`₹${amount}`}
                    className={`p-button-outlined ${selectedAmount === amount ? 'p-button-primary' : ''}`}
                    style={{ 
                      flex: isMobile ? '1 0 calc(33.333% - 8px)' : '1 0 auto',
                      minWidth: isMobile ? '0' : '70px',
                      padding: isMobile ? '0.5rem' : '0.75rem 1rem',
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      background: selectedAmount === amount ? '#3B82F6' : 'transparent',
                      color: selectedAmount === amount ? 'white' : 'inherit',
                      borderColor: selectedAmount === amount ? '#3B82F6' : '#CED4DA',
                      marginBottom: isMobile ? '4px' : '0'
                    }}
                    onClick={() => handleAmountSelect(amount)}
                    icon="pi pi-plus"
                  />
                ))}
              </div>
              <span className="p-input-icon-left p-fluid">
                <i className="pi pi-pencil"></i>
                <InputNumber
                  value={customAmount}
                  onValueChange={handleCustomAmount}
                  placeholder="Custom amount"
                  className="p-inputtext-lg"
                  min={1}
                  max={100000}
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  style={{ width: '100%' }}
                />
              </span>
            </div>

            <Divider />

            {/* Payment Button - Mobile Responsive */}
            <Button
              label={processing ? 'Processing...' : `Pay ${formatCurrency(topupAmount || 0)}`}
              icon={processing ? 'pi pi-spinner pi-spin' : 'pi pi-credit-card'}
              className="p-button-success p-button-lg"
              style={{ 
                width: '100%', 
                fontSize: isMobile ? '1rem' : '1.1rem',
                padding: isMobile ? '0.75rem' : '1rem'
              }}
              onClick={handleTopup}
              disabled={processing || !topupAmount || topupAmount <= 0}
            />

            {/* Secure Payment Info - Mobile Responsive */}
            <div className="p-d-flex p-flex-column p-flex-sm-row p-jc-between p-ai-center p-gap-2">
              <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                <i className="pi pi-lock p-mr-1"></i> Secure payment
              </div>
              <div className="p-d-flex p-gap-2">
                <i className="pi pi-cc-visa" style={{ fontSize: '1.5rem' }}></i>
                <i className="pi pi-cc-mastercard" style={{ fontSize: '1.5rem' }}></i>
                <i className="pi pi-cc-paypal" style={{ fontSize: '1.5rem' }}></i>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Stats Card - Mobile Responsive */}
        <Card className="p-shadow-4" style={{ background: '#F8F9FA' }}>
          <div className="p-d-flex p-flex-column p-flex-md-row p-jc-around p-ai-center p-text-center p-gap-2">
            <div className="p-mb-2 p-mb-md-0">
              <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                <i className="pi pi-wallet p-mr-1"></i> Balance
              </div>
              <div className="p-text-bold" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', color: '#22C55E' }}>
                {formatCurrency(walletData?.balance || 0)}
              </div>
            </div>
            {!isMobile && <Divider layout="vertical" />}
            <div className="p-mb-2 p-mb-md-0">
              <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                <i className="pi pi-bullseye p-mr-1"></i> Target
              </div>
              <div className="p-text-bold" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', color: '#3B82F6' }}>
                {formatCurrency(walletData?.targetAmount || 0)}
              </div>
            </div>
            {!isMobile && <Divider layout="vertical" />}
            <div>
              <div className="p-text-secondary" style={{ fontSize: '0.8rem' }}>
                <i className="pi pi-percentage p-mr-1"></i> Progress
              </div>
              <div className="p-text-bold" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', color: '#F59E0B' }}>
                {walletData?.targetAmount > 0 
                  ? Math.round((walletData.balance / walletData.targetAmount) * 100) 
                  : 0}%
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Payment Processing Dialog - Mobile Responsive */}
      <Dialog
        header="Processing Payment"
        visible={showPaymentDialog}
        style={{ width: isMobile ? '90%' : '350px' }}
        modal
        closable={false}
        footer={null}
      >
        <div className="p-d-flex p-flex-column p-ai-center p-gap-3 p-py-4">
          {processing && !paymentStatus && (
            <>
              <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
              <h3 className="p-m-0" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                Processing Payment
              </h3>
              <p className="p-text-secondary p-m-0 p-text-center" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Please wait while we process your payment...
              </p>
            </>
          )}
          {paymentStatus && (
            <>
              <i className={`pi ${paymentStatus.includes('successful') ? 'pi-check-circle' : 'pi-exclamation-circle'}`} 
                 style={{ fontSize: '3rem', color: paymentStatus.includes('successful') ? '#22C55E' : '#EF4444' }} />
              <h3 className="p-m-0" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                {paymentStatus}
              </h3>
            </>
          )}
        </div>
      </Dialog>

      {/* Razorpay Payment Button (Alternative - Hidden) */}
      <div style={{ display: 'none' }}>
        <form>
          <script 
            src="https://checkout.razorpay.com/v1/payment-button.js" 
            data-payment_button_id="pl_T2Ha5MN8iMBwJB" 
            async
          />
        </form>
      </div>
    </div>
  );
};

export default WalletTopup;